import { useSnackbar } from '@app/hooks/useSnackbar';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { useDashboard } from '@app/routes/Dashboard/useDashboard';
import { AlertDialog, Button, Input } from '@components/ui';
import { usePurchaseAdditionalScansMutation } from '@dieterApi/scan/usePurchaseAdditionalScansMutation';
import { useScanResultAbortMutation } from '@dieterApi/scan/useScanResultAbortMutation';
import { useCreateScanResultMutation } from '@dieterApi/scan/useScanResultCreateMutation';
import { useCookieRadarHealthQuery } from '@dieterApi/scan/useCookieRadarHealthQuery';
import { CookieRadarResult } from '@dieterApi/scan/types';
import { ScanResultData, useScanResultQuery } from '@dieterApi/scan/useScanResultQuery';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Collapse } from '@mui/material';
import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScanRow } from './ScanRow';

const POLL_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const POLL_INTERVAL_MS = 5 * 1000; // 5 seconds

function parseResults(results: string | undefined | null): CookieRadarResult | null {
  if (!results) return null;
  try {
    return JSON.parse(results) as CookieRadarResult;
  } catch {
    return null;
  }
}

export function TechnologyScan() {
  const { t, i18n } = useTranslation();
  const { user, activeCompany } = useUserWithCompany();
  const dashboard = useDashboard();
  const scanResults = activeCompany?.scanResults || [];
  const noScansLeft =
    activeCompany?.numberOfScans !== null &&
    activeCompany?.numberOfScans !== undefined &&
    activeCompany.numberOfScans <= 0;

  // --- UI state ---
  const [startUrl, setStartUrl] = useState('');
  const [dpsUrl, setDpsUrl] = useState('');
  const [scanDepth, setScanDepth] = useState<'quick' | 'standard' | 'deep'>('standard');
  const [formOpen, setFormOpen] = useState(!scanResults.length);
  const [expandedScanIds, setExpandedScanIds] = useState<Record<string, boolean>>({});
  const [pollData, setPollData] = useState<ScanResultData | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [confirmStartWithoutDpsOpen, setConfirmStartWithoutDpsOpen] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [pendingRemoveScan, setPendingRemoveScan] = useState<{ propertyId: string; isRunning: boolean } | null>(null);
  const [purchaseConfirmOpen, setPurchaseConfirmOpen] = useState(false);

  // --- Mutations & lazy query ---
  const [createScan, { loading: createScanLoading }] = useCreateScanResultMutation();
  const [abortAndRemoveScan, { loading: abortScanLoading }] = useScanResultAbortMutation();
  const [purchaseAdditionalScans, { loading: purchaseLoading }] = usePurchaseAdditionalScansMutation();
  const [fetchScanResult] = useScanResultQuery();
  const { data: cookieRadarHealthData, loading: cookieRadarHealthLoading } = useCookieRadarHealthQuery();
  const { enqueueSnackbar } = useSnackbar();
  const apolloClient = useApolloClient();
  const isCookieRadarAvailable = cookieRadarHealthData?.getCookieRadarHealth?.available === true;

  // --- Refs for polling lifecycle ---
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track the propertyId we're currently polling to avoid stale closure issues
  const pollingIdRef = useRef<string | null>(null);
  // Guard to only show completion toast once per polling session
  const hasNotifiedRef = useRef(false);

  // --- Derived data ---
  const sortedScans = useMemo(
    () => [...scanResults].sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt)),
    [scanResults]
  );

  const parsedResultsByPropertyId = useMemo(() => {
    const parsed: Record<string, CookieRadarResult | null> = {};

    sortedScans.forEach((sr) => {
      const live = pollData?.propertyId === sr.propertyId ? pollData : null;
      const resultsStr = live?.results ?? sr.results;
      parsed[sr.propertyId] = parseResults(resultsStr);
    });

    return parsed;
  }, [sortedScans, pollData]);

  // Determine running scan: check poll data first (live), then fall back to the server list
  const runningScanId = useMemo(() => {
    // If we have poll data that is still running, that's the running scan
    if (pollData && !pollData.scanFinished) return pollData.propertyId;
    // If pollData shows finished, don't pick the same scan from the stale server list
    const finishedPollId = pollData?.scanFinished ? pollData.propertyId : null;
    return sortedScans.find((sr) => !sr.scanFinished && sr.propertyId !== finishedPollId)?.propertyId ?? null;
  }, [sortedScans, pollData]);

  const hasRunningScan = runningScanId !== null;

  // --- Polling logic ---
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    pollingIdRef.current = null;
  }, []);

  const startPolling = useCallback(
    (propertyId: string) => {
      // Clean up any existing polling
      stopPolling();
      pollingIdRef.current = propertyId;
      hasNotifiedRef.current = false;

      const doPoll = async () => {
        // Guard against stale polls after cleanup
        if (pollingIdRef.current !== propertyId) return;

        try {
          const { data } = await fetchScanResult({ variables: { propertyId }, fetchPolicy: 'network-only' });
          const result = data?.getScanResult;
          if (!result || pollingIdRef.current !== propertyId) return;

          setPollData(result);

          if (result.scanFinished) {
            stopPolling();
            // Only show toast if we haven't already notified for this polling session
            if (!hasNotifiedRef.current) {
              hasNotifiedRef.current = true;
              const parsed = parseResults(result.results);
              if (parsed?.status === 'failed' || parsed?.status === 'cancelled') {
                enqueueSnackbar(t('route.scanresult.snackbar.scan_failed'), { variant: 'error' });
              } else {
                enqueueSnackbar(t('route.scanresult.snackbar.scan_completed'), { variant: 'success' });
              }
            }
            // Refetch company data so the server-side scan list reflects the completed status
            apolloClient.refetchQueries({ include: ['GetUser', 'GetCompany'] });
          }
        } catch {
          // Silently continue polling on transient errors
        }
      };

      // Immediate first poll
      doPoll();
      pollIntervalRef.current = setInterval(doPoll, POLL_INTERVAL_MS);

      // Soft-timeout guard: keep polling, but show a warning if it takes unusually long.
      pollTimeoutRef.current = setTimeout(() => {
        setTimedOut(true);
      }, POLL_TIMEOUT_MS);
    },
    [fetchScanResult, stopPolling, enqueueSnackbar, t, apolloClient]
  );

  // Cleanup on unmount
  useEffect(() => stopPolling, [stopPolling]);

  // Auto-start polling when there's a running scan and we're not already polling it
  useEffect(() => {
    if (runningScanId && pollingIdRef.current !== runningScanId) {
      setTimedOut(false);
      startPolling(runningScanId);
      // Keep the running scan expanded by default.
      setExpandedScanIds((prev) => (prev[runningScanId] ? prev : { ...prev, [runningScanId]: true }));
    } else if (!runningScanId && pollingIdRef.current) {
      stopPolling();
    }
  }, [runningScanId, startPolling, stopPolling]);

  // Clean up expansion state when scans are removed from the list
  useEffect(() => {
    const validIds = new Set(sortedScans.map((sr) => sr.propertyId));
    setExpandedScanIds((prev) => {
      const next: Record<string, boolean> = {};
      let changed = false;
      Object.entries(prev).forEach(([id, isExpanded]) => {
        if (isExpanded && validIds.has(id)) {
          next[id] = true;
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [sortedScans]);

  const toggleScanExpanded = useCallback((propertyId: string) => {
    setExpandedScanIds((prev) => ({
      ...prev,
      [propertyId]: !prev[propertyId],
    }));
  }, []);

  // --- URL validation ---
  const urlRegex = /^(https?:\/\/)[\w-]+(\.[\w-]+)+.*$/;

  const ensureProtocol = (url: string) => {
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`;
    }
    return url;
  };

  // --- Start scan handler ---
  const handleStartScan = useCallback(
    async (allowWithoutDps: boolean) => {
      if (createScanLoading) return;

      if (noScansLeft) {
        enqueueSnackbar(t('route.scanresult.no_scans_left'), { variant: 'error' });
        return;
      }

      if (hasRunningScan) {
        enqueueSnackbar(t('route.scanresult.snackbar.scan_already_running'), { variant: 'info' });
        return;
      }

      const trimmedDpsUrl = dpsUrl.trim();
      if (!trimmedDpsUrl && !allowWithoutDps) {
        setConfirmStartWithoutDpsOpen(true);
        return;
      }

      const validatedStartUrl = ensureProtocol(startUrl);
      const validatedDpsUrl = trimmedDpsUrl ? ensureProtocol(trimmedDpsUrl) : '';

      if (!urlRegex.test(validatedStartUrl) || (validatedDpsUrl && !urlRegex.test(validatedDpsUrl))) {
        enqueueSnackbar(t('route.scanresult.snackbar.invalid_url'), { variant: 'error' });
        return;
      }

      setStartUrl(validatedStartUrl);
      setDpsUrl(validatedDpsUrl);

      try {
        const response = await createScan({
          variables: { startUrl: validatedStartUrl, dpsUrl: validatedDpsUrl, force: false, scanDepth },
        });
        const propertyId = response?.data?.createScanResult?.propertyId;
        if (!propertyId) throw new Error('missing propertyId from createScanResult');

        setFormOpen(false);
        setExpandedScanIds((prev) => ({ ...prev, [propertyId]: true }));
        setTimedOut(false);
        // Polling will auto-start via the runningScanId effect
        enqueueSnackbar(t('route.scanresult.snackbar.scan_started'), { variant: 'success' });
      } catch (error) {
        console.error('Scan failed', error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage.includes('SCAN_ALREADY_RUNNING')) {
          enqueueSnackbar(t('route.scanresult.snackbar.scan_already_running'), { variant: 'info' });
        } else if (errorMessage.includes('NO_SCANS_LEFT')) {
          enqueueSnackbar(t('route.scanresult.no_scans_left'), { variant: 'error' });
        } else {
          enqueueSnackbar(t('route.scanresult.snackbar.scan_failed'), { variant: 'error' });
        }
      }
    },
    [startUrl, dpsUrl, scanDepth, createScanLoading, hasRunningScan, noScansLeft, enqueueSnackbar, createScan, t]
  );

  const handleScan = useCallback(() => {
    void handleStartScan(false);
  }, [handleStartScan]);

  const handleConfirmStartWithoutDps = useCallback(() => {
    void handleStartScan(true);
  }, [handleStartScan]);

  // --- Purchase additional scans handler ---
  const handlePurchaseAdditionalScans = useCallback(async () => {
    try {
      const result = await purchaseAdditionalScans();
      if (result.data?.purchaseAdditionalScans.success) {
        enqueueSnackbar(t('route.scanresult.purchase_success'), { variant: 'success' });
        apolloClient.refetchQueries({ include: ['GetUser', 'GetCompany'] });
      }
    } catch (error) {
      console.error('Purchase failed', error);
      enqueueSnackbar(t('route.scanresult.purchase_failed'), { variant: 'error' });
    }
  }, [purchaseAdditionalScans, enqueueSnackbar, t, apolloClient]);

  // --- Remove / cancel scan handler ---
  const handleRequestRemoveScan = useCallback(
    (propertyId: string) => {
      const scan = sortedScans.find((sr) => sr.propertyId === propertyId);
      const isRunning = scan ? !scan.scanFinished : false;
      setPendingRemoveScan({ propertyId, isRunning });
      setRemoveConfirmOpen(true);
    },
    [sortedScans]
  );

  const handleRemoveScan = useCallback(
    async (propertyId: string) => {
      try {
        await abortAndRemoveScan({ variables: { propertyId } });

        // If we were polling this scan, stop
        if (pollingIdRef.current === propertyId) {
          stopPolling();
          setPollData(null);
        }

        setExpandedScanIds((prev) => {
          if (!prev[propertyId]) return prev;
          const next = { ...prev };
          delete next[propertyId];
          return next;
        });

        enqueueSnackbar(t('route.scanresult.snackbar.scan_removed'), { variant: 'success' });
      } catch {
        enqueueSnackbar(t('route.scanresult.snackbar.scan_abort_failed'), { variant: 'error' });
      }
    },
    [abortAndRemoveScan, stopPolling, enqueueSnackbar, t]
  );

  const handleConfirmRemoveScan = useCallback(() => {
    if (!pendingRemoveScan) return;
    void handleRemoveScan(pendingRemoveScan.propertyId);
  }, [pendingRemoveScan, handleRemoveScan]);

  const handleOpenPrintView = useCallback((propertyId: string) => {
    const printUrl = `/scan-result-print/${encodeURIComponent(propertyId)}`;
    window.open(printUrl, '_blank', 'noopener,noreferrer');
  }, []);

  const handleOpenReportView = useCallback((propertyId: string) => {
    const reportUrl = `/scan-result-report/${encodeURIComponent(propertyId)}`;
    window.open(reportUrl, '_blank', 'noopener,noreferrer');
  }, []);

  if (cookieRadarHealthLoading) {
    return (
      <div className="flex flex-col gap-5">
        <p className="bg-blue-50 border-l-4 border-blue-400 text-blue-700 p-4" role="status">
          {t('route.scanresult.checking_service')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 h-full min-h-0">
      {/* Header + toggle form */}
      <div className="flex items-center gap-8">
        <span className="text-base font-medium">{t('route.scanresult.heading')}</span>
        {isCookieRadarAvailable && (
          <button className="text-primary-root hover:text-primary-700" onClick={() => setFormOpen(!formOpen)}>
            {formOpen ? <RemoveCircleOutlineIcon /> : <AddCircleOutlineIcon />}
          </button>
        )}
      </div>

      {/* Service unavailable banner — shown above the scan list, new scan form is hidden */}
      {!isCookieRadarAvailable && (
        <div className="bg-red-50 border-l-4 border-red-400 text-red-700 p-4" role="alert">
          <p className="font-medium">{t('route.scanresult.service_unavailable_title')}</p>
          <p className="mt-1">{t('route.scanresult.service_unavailable_description')}</p>
        </div>
      )}

      {/* New scan form — hidden when service is unavailable */}
      <Collapse in={formOpen && isCookieRadarAvailable}>
        <div className="flex flex-col gap-4 mb-8">
          <p>{t('route.scanresult.description')}</p>

          <Input
            textSize="small"
            placeholder={t('route.scanresult.placeholder.start_url')}
            value={startUrl}
            onChange={(e) => setStartUrl(e.target.value)}
          />
          <Input
            textSize="small"
            placeholder={t('route.scanresult.placeholder.dps_url')}
            value={dpsUrl}
            onChange={(e) => setDpsUrl(e.target.value)}
          />

          {/* Scan depth selector */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              {t('route.scanresult.details.scan_depth_label')}
            </label>
            <div className="flex gap-3">
              {(['quick', 'standard', 'deep'] as const).map((depth) => (
                <label key={depth} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="scanDepth"
                    value={depth}
                    checked={scanDepth === depth}
                    onChange={(e) => setScanDepth(e.target.value as 'quick' | 'standard' | 'deep')}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">{t(`route.scanresult.details.scan_depth_${depth}`)}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {t(`route.scanresult.details.scan_depth_${scanDepth}_description`)}
            </p>
          </div>

          {/*
          <p>
            <span className="text-lg">{t('route.scanresult.scans_left_label')} </span>
            <span className="bg-gray-200 px-2 py-1 rounded-md text-lg text-gray-500">
              {activeCompany?.numberOfScans}
            </span>
          </p>
          */}

          {activeCompany?.numberOfScans !== null && activeCompany?.numberOfScans !== undefined && (
            <p className="text-sm text-gray-600">
              {t('route.scanresult.scans_left_label')}{' '}
              <span className="font-semibold">{activeCompany.numberOfScans}</span>
            </p>
          )}

          <Button
            onClick={handleScan}
            disabled={createScanLoading || user?.isReadOnly || hasRunningScan || noScansLeft}
            spinner={createScanLoading}
          >
            {t('route.scanresult.start_scan')}
          </Button>

          {hasRunningScan && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              {t('route.scanresult.running_scan_notice')}
            </p>
          )}
        </div>
      </Collapse>

      {noScansLeft && (
        <div className="bg-amber-50 border-l-4 border-amber-400 text-amber-800 p-4" role="alert">
          <p className="font-medium">{t('route.scanresult.no_scans_left')}</p>
          <p className="mt-1 text-sm">{t('route.scanresult.purchase_description')}</p>
          <button
            onClick={() => setPurchaseConfirmOpen(true)}
            disabled={purchaseLoading}
            className="mt-3 inline-flex items-center px-4 py-2 bg-primary-root text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            {purchaseLoading ? t('common.loading') : t('route.scanresult.purchase_button')}
          </button>
        </div>
      )}

      {/* CTA: Generate privacy policy based on scan results */}
      {sortedScans.length > 0 &&
        !user?.isSubscriber &&
        sortedScans.some((sr) => {
          const score = parsedResultsByPropertyId[sr.propertyId]?.site?.privacy_score;
          return score && score !== 'A';
        }) && (
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-5 flex flex-col gap-3">
            <p className="text-base font-medium text-primary-900">{t('route.scanresult.cta_dse.title')}</p>
            <p className="text-sm text-primary-800">{t('route.scanresult.cta_dse.text')}</p>
            <button
              onClick={() => dashboard.create('app-dse-privacy-policy-create')}
              className="self-start inline-flex items-center px-5 py-2.5 bg-primary-root text-white rounded-lg hover:bg-primary-700 text-sm font-medium transition-colors"
            >
              {t('route.scanresult.cta_dse.button')}
            </button>
          </div>
        )}

      {/* Scan list */}
      {sortedScans.length > 0 && (
        <div className="flex flex-col gap-2 flex-1 min-h-0">
          <span className="text-sm font-medium text-gray-700">{t('route.scanresult.scan_history')}</span>
          <div className="flex flex-col gap-1.5 flex-1 min-h-0 overflow-y-auto pr-1">
            {sortedScans.map((sr) => {
              // Use live poll data for the running scan to show up-to-date status
              const live = pollData?.propertyId === sr.propertyId ? pollData : null;
              const parsed = parsedResultsByPropertyId[sr.propertyId] ?? null;
              const isExpanded = !!expandedScanIds[sr.propertyId];

              return (
                <ScanRow
                  key={sr.propertyId}
                  sr={sr}
                  live={live}
                  parsed={parsed}
                  isExpanded={isExpanded}
                  noScansLeft={noScansLeft}
                  timedOut={timedOut}
                  runningScanId={runningScanId}
                  abortScanLoading={abortScanLoading}
                  onToggleExpanded={toggleScanExpanded}
                  onOpenReportView={handleOpenReportView}
                  onOpenPrintView={handleOpenPrintView}
                  onRequestRemoveScan={handleRequestRemoveScan}
                />
              );
            })}
          </div>
        </div>
      )}

      <AlertDialog
        open={confirmStartWithoutDpsOpen}
        setOpen={setConfirmStartWithoutDpsOpen}
        message={t('route.scanresult.confirm_start_without_dps')}
        acceptText={t('common.yes')}
        onAccept={handleConfirmStartWithoutDps}
      />

      <AlertDialog
        open={removeConfirmOpen}
        setOpen={(open) => {
          setRemoveConfirmOpen(open);
          if (!open) setPendingRemoveScan(null);
        }}
        message={t(
          pendingRemoveScan?.isRunning
            ? 'route.scanresult.confirm_abort_and_remove_scan'
            : 'route.scanresult.confirm_remove_scan'
        )}
        onAccept={handleConfirmRemoveScan}
      />

      <AlertDialog
        open={purchaseConfirmOpen}
        setOpen={setPurchaseConfirmOpen}
        message={t('route.scanresult.purchase_confirm')}
        acceptText={t('route.scanresult.purchase_confirm_accept')}
        onAccept={handlePurchaseAdditionalScans}
      />
    </div>
  );
}
